import { quetions, quetionsTwo } from './../constants/survey';
import axios, { AxiosError } from 'axios';
import { Activity, GroupedActivities, Plan } from 'types/general.type';
import DOMPurify from 'dompurify';
import { Trustee } from 'service/dto/trustee.dto';
import { STEPS, getStepPage } from 'constants/steps';

export const errorHandler = (err: AxiosError<Error>) => {
  if (err.response?.status === 400) {
    const errorType = typeof err.response?.data?.message;
    if (errorType !== 'string') {
      throw new Error((err.response.data.message || [])[0]);
    }
    throw new Error(err.response?.data?.message as string);
  }
  if (axios.isAxiosError(err)) {
    throw new Error(
      (err.response?.data?.message || 'Operation Timeout') as string
    );
  }
  throw new Error(
    'Error processing request, please check your internet connection'
  );
};

export const createWrapperAndAppendToBody = (wrapperId: string) => {
  const wrapperElement = document.createElement('div');
  wrapperElement.setAttribute('id', wrapperId);
  document.body.appendChild(wrapperElement);
  return wrapperElement;
};

export const formatToMoney = (
  numberOrString: string | number | undefined,
  decimalPlaces = 2
) => {
  if (!numberOrString) {
    return 'Invalid input';
  }
  // Ensure that we have a valid number
  const number =
    typeof numberOrString === 'string'
      ? parseFloat(numberOrString)
      : numberOrString;

  if (isNaN(number)) {
    // Handle invalid input
    return 'Invalid input';
  }

  return number.toLocaleString('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
};

export const passwordStrengthVariables = (values: any) => {
  if (values.password.trim().length > 0) {
    let strengthConditionsFulfilled = 0;

    if (/\d/.test(values.password)) {
      strengthConditionsFulfilled += 1;
    }

    if (/[A-Z]/.test(values.password)) {
      strengthConditionsFulfilled += 1;
    }

    if (/[a-z]/.test(values.password)) {
      strengthConditionsFulfilled += 0.5;
    }

    // eslint-disable-next-line no-useless-escape
    if (/[!@#$%^&*()_+{}\[\]:;<>,.?~|]/.test(values.password)) {
      strengthConditionsFulfilled += 0.5;
    }

    if (values.password.trim().length >= 8) {
      strengthConditionsFulfilled += 1;
    }

    return {
      hasStrengthIndicator: true,
      totalStrengthConditions: 4,
      strengthConditionsFulfilled
    };
  }

  return {
    hasStrengthIndicator: false,
    totalStrengthConditions: 4,
    strengthConditionsFulfilled: 0
  };
};

export const determinePathName = (pathname: string) => {
  if (pathname === '/dashboard/profile') {
    return 'dashboard';
  }
  const parts = pathname.split('/');
  const dashboardIndex = parts[2] || parts[1];
  return dashboardIndex;
};

export const determineRecommendedPlan = (
  userSelections: any,
  plans: Plan[]
) => {
  if (plans.length) {
    const options = [...quetionsTwo, ...quetions];
    // Convert user selections object to an array of selected options
    const userSelectedOptions = Object.keys(userSelections).filter(
      fieldName => userSelections[fieldName] //If answer is yes select the plan for user
    );

    // Calculate a score for each plan based on user's selections
    const planScores = plans.map(plan => {
      let score = 0;

      userSelectedOptions.forEach(selectedOption => {
        const selectedOptionTitle = options.find(
          opt => opt.fieldName === selectedOption
        );
        const packageItem = plan.packages.find(
          pkg => pkg.surveyKey === selectedOptionTitle?.fieldName
        );

        if (packageItem) {
          score += 1; // You can adjust this scoring logic as needed
        }
      });

      return { planId: plan.planId, score };
    });

    // Sort plans by score in descending order
    planScores.sort((a, b) => b.score - a.score);

    // Get the plan with the highest score (recommended plan)
    const recommendedPlanId = planScores[0].planId;

    // Update the isRecommended flag for each plan
    plans.forEach(plan => {
      plan.isRecommended = plan.planId === recommendedPlanId ? 1 : 0;
      plan.isSelectedPlan = plan.planId === recommendedPlanId ? 1 : 0;
    });

    // Return the recommended plan object
    const recommendedPlan = plans.find(
      plan => plan.planId === recommendedPlanId
    );
    return recommendedPlan;
  }
};

export const selectPlan = (planId: number, plans: Plan[]) => {
  if (plans) {
    plans.forEach(plan => {
      plan.isSelectedPlan = plan.planId === planId ? 1 : 0;
    });
    // Return the recommended plan object
    const recommendedPlan = plans.find(plan => plan.planId === planId);
    return recommendedPlan;
  }
};

export const getImageExtension = (path: string): string | null => {
  const regex = /\.(jpg|jpeg|png|gif)$/i;
  const extensionMatch = path.match(regex);
  return extensionMatch ? extensionMatch[0] : null;
};

export const showRemoveButton = (
  index: number,
  id: number | null,
  showButton: boolean
) => {
  if (showButton && id) {
    return true;
  } else if (index === 0) {
    return false;
  } else {
    return true;
  }
};

export const determineStepCount = (currentStep: number) => {
  if (currentStep <= 4) {
    return 1;
  } else if (currentStep >= 5 && currentStep <= 10) {
    return 2;
  } else {
    return 3;
  }
};

export const printTable = () => {
  window.print();
};

export const groupActivities = (activities: Activity[]): GroupedActivities => {
  const todayDate = new Date();
  const yesterdayDate = new Date(todayDate);
  yesterdayDate.setDate(todayDate.getDate() - 1);

  const groupedActivities: GroupedActivities = [];

  const activityGroups: { [key: string]: Activity[] } = {
    Today: [],
    Yesterday: []
  };

  activities.forEach(activity => {
    const activityDate = new Date(activity.createdAt).toDateString();

    if (activityDate === todayDate.toDateString()) {
      activityGroups['Today'].push(activity);
    } else if (activityDate === yesterdayDate.toDateString()) {
      activityGroups['Yesterday'].push(activity);
    } else {
      if (!activityGroups[activityDate]) {
        activityGroups[activityDate] = [];
      }
      activityGroups[activityDate].push(activity);
    }
  });

  // Convert the activityGroups object into an array of objects
  for (const key in activityGroups) {
    if (activityGroups[key].length > 0) {
      const group = { [key]: activityGroups[key] };
      groupedActivities.push(group);
    }
  }

  return groupedActivities;
};

export const createMarkup = (html: string) => {
  return {
    __html: DOMPurify.sanitize(html)
  };
};

export const isObjectEmpty = (
  obj: { [x: string]: string },
  excludedFields: string | string[]
) => {
  for (const key in obj) {
    if (
      Object.prototype.hasOwnProperty.call(obj, key) &&
      !excludedFields.includes(key) &&
      obj[key] !== null &&
      obj[key] !== undefined &&
      obj[key] !== ''
    ) {
      return false;
    }
  }
  return true;
};

export const hasEmptyValue = (
  arrayOfObjects: any,
  excludedFields: string[]
) => {
  for (const obj of arrayOfObjects) {
    if (isObjectEmpty(obj, excludedFields)) {
      return true;
    }
  }
  return false;
};

export const StringFormater = (string: any) => {
  if (
    string &&
    string !== null &&
    string !== undefined &&
    string !== '' &&
    string !== 'null null null'
  ) {
    return string;
  } else {
    return 'N/A';
  }
};

export const getObjectCategories = (item: Trustee) => {
  const categories: any[] = [];

  if (item.banks.length > 0) {
    categories.push('Bank');
  }
  if (item.stocks.length > 0) {
    categories.push('Stock');
  }
  if (item.pensions.length > 0) {
    categories.push('Pension');
  }
  if (item.physicalAssets.length > 0) {
    categories.push('Physical Asset');
  }
  if (item.employeeBenefits.length > 0) {
    categories.push('Employee Benefit');
  }
  if (item.bonds.length > 0) {
    categories.push('Bond');
  }

  return categories;
};

export const cutText = (text: string, maxLength: number = 21) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength - 3) + '...';
  } else {
    return text;
  }
};

const getNumberBefore = (planAllowPage: number[], targetNumber: number) => {
  const index = planAllowPage.indexOf(targetNumber);
  // Check if the target number is in the array and not the first element
  if (index > 0) {
    return planAllowPage[index - 1];
  } else {
    return null; // If the target number is not found or is the first element
  }
};

const getNumberAfter = (planAllowPage: number[], targetNumber: number) => {
  const index = planAllowPage.indexOf(targetNumber);

  // Check if the target number is in the array and not the last element
  if (index !== -1 && index < planAllowPage.length - 1) {
    return planAllowPage[index + 1];
  } else {
    return null; // If the target number is not found or is the last element
  }
};

export const findPreviousPage = (
  planAllowPage: number[],
  currentPage: number
) => {
  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const targetNumber = getNumberBefore(planAllowPage, currentPage);
  if (targetNumber && targetNumber > 0) {
    const previousPage = getStepPage(targetNumber);
    if (parentPath === 'dashboard') {
      return `/dashboard/onboarding/${previousPage}`;
    }
    return `/on-boarding/${previousPage}`;
  }
  return `/dashboard/onboarding/basic-info`;
};

export const findNextPage = (planAllowPage: number[], currentPage: number) => {
  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];
  if (currentPage === STEPS.TRUSTEE_INFORMATION) {
    //If the page is equal to last page the take user to welcome page
    if (parentPath === 'dashboard') {
      return '/dashboard';
    }
    return '/on-boarding/welcome';
  }

  const targetNumber = getNumberAfter(planAllowPage, currentPage);
  // If we are able to find the page then continue
  if (targetNumber && targetNumber > 0) {
    const nextPage = getStepPage(targetNumber);
    if (parentPath === 'dashboard') {
      return `/dashboard/onboarding/${nextPage}`;
    }
    return `/on-boarding/${nextPage}`;
  }
  return '/dashboard'; // Return null if the target number is not found or if it is the first element in the array
};

export const isValidNumber = (value: any) => {
  if (!/^[0-9]+$/.test(value)) {
    return false;
  }
  return true;
};

export const checkTrusteeAssignment = (
  trustee: Trustee,
  currentTrusteeId: number
) => {
  if (!trustee) {
    return { alreadyAssignedToYou: false, assignedToAnotherUser: false };
  }

  const alreadyAssignedToYou = trustee.id === currentTrusteeId;
  const assignedToAnotherUser = !alreadyAssignedToYou;

  return {
    alreadyAssignedToYou,
    assignedToAnotherUser,
    shouldDisabled: alreadyAssignedToYou || assignedToAnotherUser
  };
};

export const getTrusteeIds = (data: Record<string, any>[]) => {
  const trusteeIds: number[] = [];
  if (data && Array.isArray(data)) {
    data.forEach(item => {
      if (
        Object.prototype.hasOwnProperty.call(item, 'trustee') &&
        item.trustee &&
        Object.prototype.hasOwnProperty.call(item.trustee, 'id')
      ) {
        trusteeIds.push(item.id);
      }
    });
  }

  return trusteeIds;
};

export const getProductBoughtName = (planId: number | null): string => {
  switch (planId) {
    case 1:
      return 'iWill';
      break;
    case 2:
      return 'iWill Plus';
      break;
    case 3:
      return 'iWill Xtra';
      break;
    case 4:
      return 'iWill Premium';
      break;
    default:
      return 'Unknown';
      break;
  }
};

export const isLeadWayEmail = (email: string): boolean => {
  const domain = email.split('@')[1];
  return domain === 'leadway.com';
};

export const truncateText = (fullText: string, truncationLength?: number) => {
  if (fullText) {
    const truncationFactor = truncationLength || 200;
    if (fullText.length <= truncationFactor) {
      return fullText;
    }
    return `${fullText.slice(0, truncationFactor)}...`;
  }
  return '';
};
